import React, { useCallback, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import RegisterForm from './RegisterForm';
import SendParentEmailForm from '../SendParentEmailForm';
import Layout from '../../../layout/Layout';
import { useTranslation } from 'react-i18next';
import { differenceInYears, startOfToday } from 'date-fns';
import authService from '../../../../auth/authService';
import Newsletter from './Newsletter';

const newsletterReducer = (state, newState) => ({ ...state, ...newState });

const initialState = {
  newsletterVisited: false,
  username: null,
  email: null,
  password: null,
  privacyApproval: false,
  newsletterApproval: false,
  marketingApproval: false,
  marketingEducation: false,
  marketingProfessional: false,
};

const UserRegister = (props) => {
  const { birthday } = props;
  const history = useHistory();
  const [teenInfo, setTeenInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('register');

  const [userData, setUserData] = useReducer(newsletterReducer, initialState);
  const [signUpRoute, setSignUpRoute] = useState('register');

  const onRegisterTeenCallback = (email, err) => {
    if (err && err.code !== 'unconfirmed_teen') {
      setLoading(false);
      setError(t(err.code));
    } else {
      // if come from a coppa flow show the right login form
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('loginCoppa')) {
        urlParams.set('loginCoppa', 'adult-login');
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + '?' + urlParams.toString() + window.location.hash
        );
      }
      history.push({
        pathname: '/login',
        query: { parentEmail: email },
      });
    }
  };

  const handleTeenRegistration = (info) => {
    setLoading(true);
    const params = {
      username: teenInfo.username,
      email: teenInfo.email,
      password: teenInfo.password,
      user_metadata: {
        parent_email: info.email,
        birthday: birthday,
      },
    };

    authService.signup(params, onRegisterTeenCallback.bind(null, info.email));
  };

  const onRegisterCallbackError = useCallback(
    (err) => {
      setLoading(false);
      setSignUpRoute('register');
      if (err.code === 'extensibility_error') {
        setError(t(err.description));
      } else {
        setError(t(err.code));
      }
    },
    [t]
  );

  const handleAdultRegistration = useCallback(
    (data) => {
      const updatedData = { ...userData, ...data };
      const {
        marketingEducation,
        marketingProfessional,
        username,
        email,
        password,
        privacyApproval,
        newsletterApproval,
        marketingApproval,
      } = updatedData;
      const marketingTags = [
        marketingEducation ? 'marketing_educational' : null,
        marketingProfessional ? 'marketing_professional' : null,
      ].filter((t) => !!t);

      const params = {
        username: username,
        email: email,
        password: password,
        user_metadata: {
          privacy_approval: privacyApproval.toString(),
          terms_and_conditions: privacyApproval.toString(),
          newsletter_approval: newsletterApproval.toString(),
          marketing_approval: marketingApproval.toString(),
          // The name of the field was requested as-is
          tag_string: marketingTags.join(','),
        },
      };

      authService.signupAndLogin(params, onRegisterCallbackError);
      // Clear stored birthday date
      window.sessionStorage.removeItem('birthday');
    },
    [userData, onRegisterCallbackError]
  );

  const handleUserRegistration = useCallback(
    (data) => {
      if (differenceInYears(startOfToday(), new Date(birthday)) >= 16) {
        if (userData.newsletterVisited || props.customDomainInfo?.organization_name) {
          setLoading(true);
          setError('');
          setUserData({ ...data });

          handleAdultRegistration(data);
        } else {
          setSignUpRoute('newsletter');
          setUserData({
            username: data.username,
            email: data.email,
            password: data.password,
            privacyApproval: data.privacyApproval,
          });
        }
      } else {
        setLoading(true);
        setError('');

        setTeenInfo(data);
        setLoading(false);
      }
    },
    [birthday, userData.newsletterVisited, props.customDomainInfo, handleAdultRegistration]
  );

  const handleNewsletterSubmit = useCallback(
    (data) => {
      setLoading(true);
      setUserData({ ...data });
      handleAdultRegistration(data);
    },
    [handleAdultRegistration]
  );

  const renderRegister = useCallback(() => {
    switch (signUpRoute) {
      case 'newsletter':
        return (
          <Layout>
            <Newsletter setUserData={setUserData} onSubmit={handleNewsletterSubmit} loading={loading} />
          </Layout>
        );
      case 'register':
      default:
        return (
          <Layout onBack={() => history.push('/login')}>
            <RegisterForm
              onSubmit={handleUserRegistration}
              error={error}
              loading={loading}
              showConsent={
                !props.customDomainInfo?.organization_name &&
                differenceInYears(startOfToday(), new Date(birthday)) >= 16
              }
              customDomainInfo={props.customDomainInfo}
              userData={userData}
            />
          </Layout>
        );
    }
  }, [
    signUpRoute,
    history,
    handleUserRegistration,
    error,
    loading,
    birthday,
    props.customDomainInfo,
    handleNewsletterSubmit,
    userData,
  ]);

  return teenInfo ? (
    <Layout
      onBack={() => {
        setTeenInfo(null);
        setError('');
        setLoading(false);
      }}
    >
      <SendParentEmailForm
        onSubmit={handleTeenRegistration}
        error={error}
        loading={loading}
        invalidEmail={teenInfo.email}
      />
    </Layout>
  ) : (
    renderRegister()
  );
};

UserRegister.propTypes = {
  birthday: PropTypes.string,
  customDomainInfo: PropTypes.object,
};

export default UserRegister;
